import { useState } from 'react';
import { ArrowRight, ArrowLeft, Info } from 'lucide-react';
import type { ParetoItem } from '../types';

interface ContributionViewProps {
  item: ParetoItem;
  itemIndex: number;
  totalItems: number;
  onContributionUpdate: (value: number | null) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  progress: number;
}

export function ContributionView({
  item,
  itemIndex,
  totalItems,
  onContributionUpdate,
  onNext,
  onBack,
  canProceed,
  progress,
}: ContributionViewProps) {
  const [inputValue, setInputValue] = useState<string>(item.contribution?.toString() || '');

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const numValue = value.trim() === '' ? null : parseFloat(value);
    if (numValue !== null && !isNaN(numValue)) {
      onContributionUpdate(numValue);
    } else {
      onContributionUpdate(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[var(--text-sec)] mb-2">
            <span>Item {itemIndex + 1} of {totalItems}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
            <div
              className="bg-[var(--slate)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current Item */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h2 className="text-xl font-bold text-[var(--text-pri)]">{item.name}</h2>
        </div>

        {/* Info */}
        <div className="mb-6">
          <div className="flex items-start gap-2 mb-4">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">Contribution Score</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                Estimate this item's relative contribution to the overall outcome. Use any consistent numeric scale (1-100). Higher = more contribution.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Contribution score for <strong>{item.name}</strong>
            </label>
            <input
              type="number"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="e.g., 90"
              min="0"
              step="1"
              className="w-full px-4 py-3 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              autoFocus
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Example: 90 (high contribution)</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {itemIndex > 0 && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
          )}
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed transition-colors"
          >
            {itemIndex === totalItems - 1 ? 'View Results' : 'Next'}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

